import { Switch, Route, Redirect } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { AccountInfo } from '@azure/msal-browser';
import { appConfig } from '../config/appConfig';
import ComingSoon from '../components/shared/ComingSoon';
import { NotFound } from '../pages/StatusPages/NotFound';
import CheckAuthorization from '../pages/StatusPages/CheckAuthorization';
import { Unauthorized } from '../pages/StatusPages/Unauthorized';
import { UserWizard } from '../pages/UserWizard/UserWizard';
import SearchProperty from '../pages/Search/SearchProperty';
import ThemeManager from '../pages/ThemeManager';
import LookUpContext from '../context/LookUpContext';
import OverviewCheckUp from '../pages/SideMenuComponents/OverviewCheckUp';
import DocSignFrame from '../pages/DocSign/DocSignFrame';
import DocPreview from '../pages/DocSign/DocPreview';
import CompanyList from '../pages/AdminPanel/Company/CompanyList';
import Pricing from '../pages/Static/Pricing';
import TermsOfUse from '../pages/Static/TermsOfUse';
import Invitations from '../pages/SideMenuComponents/Invitations';
import PrivacyPolicy from '../pages/Static/PrivacyPolicy';
import DataLinkAndTransaction from '../pages/DataLinkAndTransaction/DataLinkAndTransaction';
import ViewExploreData from '../pages/ExploreData';
import TemplateContext from '../context/TemplateContext';
import UserList from '../pages/AdminPanel/User/UserList';
import PermissionService from '../services/Permissions';
import PropertyRoute from './PropertyRoute';
import AccountRoute from './AccountRoute';
import Profile from '../pages/Profile';
import AnalyticsDashboard from '../pages/Analytics';
import BuildingDirectory from '../pages/BuildingDirectory';
import TradeProbability from '../pages/TradeProbability';
import TransactionData from '../pages/TransactionData';
import DocumentsList from '../pages/Documents/DocumentsList';
import DocumentViewer from '../pages/Documents/DocumentViewer';
import DocumentSettings from '../pages/Documents/DocumentSettings';
import ProjectDocumentViewer from '../pages/Documents/ProjectDocumentViewer';
import RoleManagementPage from '../pages/AdminPanel/RoleManagement/RoleManagementPage';
import UserManagementPage from '../pages/AdminPanel/User/UserManagementPage';
import LoginPage from '../pages/Login/LoginPage';
import AcceptInvitation from '../pages/AcceptInvitation/AcceptInvitation';
import { getLocalUser, isLocallyAuthenticated, useLocalAuth, fetchAndCacheMsalRole } from '../auth/LocalAuthService';
import { setToken } from '../auth/UserService';
import { Scopes } from '../auth/authConfig';
import { RolesEnum } from '../constants/roles';

const AdminOnlyRoute = ({ children }: any) => {
    const localAuth = useLocalAuth();
    const localRole = Number(getLocalUser()?.role ?? -1);
    const isLocalAdmin = isLocallyAuthenticated()
        ? (localRole === RolesEnum.Admin || localRole === RolesEnum.SuperAdmin)
        : false;
    const isMsalAdmin = PermissionService.canViewAdminPanelMenu();
    const allowed = localAuth ? isLocalAdmin : isMsalAdmin;
    return allowed ? <>{children}</> : <Unauthorized />;
};

const Routes = () => {
    const [, setRoleTick] = useState(0);
    const { instance, accounts } = useMsal();

    useEffect(() => {
        const isLocalAuth =
            process.env.REACT_APP_BYPASS_MSAL === 'true' ||
            process.env.NODE_ENV === 'development';

        if (isLocalAuth || accounts.length === 0) return;

        // Acquire a fresh MSAL token so the backend can identify the user,
        // then fetch and cache their role from the DB.
        const account = accounts[0] as AccountInfo;
        instance
            .acquireTokenSilent({ scopes: Scopes.api, account })
            .then(result => {
                setToken(result.accessToken);
                return fetchAndCacheMsalRole();
            })
            .then(() => setRoleTick(n => n + 1))
            .catch(() => {
                // silent refresh failed — redirect to login
                instance.acquireTokenRedirect({ scopes: Scopes.api });
            });
    }, [accounts, instance]);

    const localAuth = useLocalAuth();
    const localRole = Number(getLocalUser()?.role ?? -1);
    const isLocalAdminRole = localRole === RolesEnum.Admin || localRole === RolesEnum.SuperAdmin;
    const isDealRoomOnly = (localAuth && isLocallyAuthenticated())
        ? !isLocalAdminRole
        : PermissionService.isDealRoomOnlyMsalUser();

    if (isDealRoomOnly) {
        return (
            <LookUpContext>
                <TemplateContext>
                    <Switch>
                        <Route exact path="/login" component={LoginPage} />
                        <Route exact path="/accept-invitation" component={AcceptInvitation} />
                        <Route
                            exact
                            path="/documents"
                            component={() => (
                                <CheckAuthorization>
                                    <DocumentsList />
                                </CheckAuthorization>
                            )}
                        />
                        <Route
                            exact
                            path="/documents/project-file/:fileId"
                            component={() => (
                                <CheckAuthorization>
                                    <ProjectDocumentViewer />
                                </CheckAuthorization>
                            )}
                        />
                        <Route
                            exact
                            path="/documents/:docId"
                            component={() => (
                                <CheckAuthorization>
                                    <DocumentViewer />
                                </CheckAuthorization>
                            )}
                        />
                        <Route
                            exact
                            path="/profile"
                            component={() => (
                                <CheckAuthorization>
                                    <Profile />
                                </CheckAuthorization>
                            )}
                        />
                        <Route path="*">
                            <Redirect to="/documents" />
                        </Route>
                    </Switch>
                </TemplateContext>
            </LookUpContext>
        );
    }

    return (
        <LookUpContext>
            <TemplateContext>
                <Switch>
                    <Route exact path="/login" component={LoginPage} />
                    <Route exact path="/accept-invitation" component={AcceptInvitation} />
                    <Route exact path="/">
                        <Redirect to={appConfig.homePage} />
                    </Route>
                    <Route exact path="/SignedUp" component={OverviewCheckUp} />
                    <Route exact path="/preview/:id" component={DocPreview} />
                    <Route exact path="/pricing" component={Pricing} />
                    <Route exact path="/doc-sign" component={DocSignFrame} />
                    <Route exact path="/terms-of-use" component={TermsOfUse} />
                    <Route exact path="/privacy-policy" component={PrivacyPolicy} />
                    <Route exact path="/search-property" component={SearchProperty} />
                    <Route exact path="/theme" component={ThemeManager} />
                    <Route exact path="/unauthorized" component={Unauthorized} />
                    <Route exact path="/coming-soon" component={ComingSoon} />
                    <Route
                        exact
                        path="/admin-panel/company"
                        component={() => (
                            <CheckAuthorization>
                                <AdminOnlyRoute>
                                    <CompanyList />
                                </AdminOnlyRoute>
                            </CheckAuthorization>
                        )}
                    />
                    <Route
                        exact
                        path="/admin-panel/company/theme/:id"
                        component={() => (
                            <CheckAuthorization>
                                <AdminOnlyRoute>
                                    <ThemeManager />
                                </AdminOnlyRoute>
                            </CheckAuthorization>
                        )}
                    />
                    <Route
                        exact
                        path="/admin-panel/users/:id"
                        component={() => (
                            <CheckAuthorization>
                                <AdminOnlyRoute>
                                    <div style={{ paddingTop: 100, padding: '100px 24px 24px 24px' }}>
                                        <UserList />
                                    </div>
                                </AdminOnlyRoute>
                            </CheckAuthorization>
                        )}
                    />
                    <Route
                        exact
                        path="/analytics"
                        component={AnalyticsDashboard}
                    />
                    <Route exact path="/overview" component={OverviewCheckUp} />
                    <Route exact path="/invitations" component={Invitations} />
                    <Route exact path="/data-link-or-transaction" component={DataLinkAndTransaction} />
                    <Route
                        exact
                        path="/data-analytics"
                        component={() => (
                            <CheckAuthorization>
                                <ViewExploreData />
                            </CheckAuthorization>
                        )}
                    />
                    <Route path="/property">
                        <PropertyRoute />
                    </Route>
                    <Route exact path="/profile" component={Profile} />
                    <Route path="/account-settings">
                        <AccountRoute />
                    </Route>
                    <Route
                        exact
                        path="/documents"
                        component={() => (
                            <CheckAuthorization>
                                <DocumentsList />
                            </CheckAuthorization>
                        )}
                    />
                    <Route
                        exact
                        path="/manage-documents"
                        component={() => (
                            <CheckAuthorization>
                                <AdminOnlyRoute>
                                    <DocumentSettings />
                                </AdminOnlyRoute>
                            </CheckAuthorization>
                        )}
                    />
                    <Route
                        exact
                        path="/documents/project-file/:fileId"
                        component={() => (
                            <CheckAuthorization>
                                <ProjectDocumentViewer />
                            </CheckAuthorization>
                        )}
                    />
                    <Route
                        exact
                        path="/documents/:docId"
                        component={() => (
                            <CheckAuthorization>
                                <DocumentViewer />
                            </CheckAuthorization>
                        )}
                    />
                    <Route
                        exact
                        path="/roles/settings"
                        component={() => (
                            <CheckAuthorization>
                                <AdminOnlyRoute>
                                    <RoleManagementPage />
                                </AdminOnlyRoute>
                            </CheckAuthorization>
                        )}
                    />
                    <Route
                        exact
                        path="/users/settings"
                        component={() => (
                            <CheckAuthorization>
                                <AdminOnlyRoute>
                                    <UserManagementPage />
                                </AdminOnlyRoute>
                            </CheckAuthorization>
                        )}
                    />
                    <Route exact path="/building-directory/:id?" component={BuildingDirectory} />
                    <Route exact path="/trade-probability/:id?" component={TradeProbability} />
                    <Route exact path="/transaction-data/:id?" component={TransactionData} />
                    <Route
                        exact
                        path="/user-wizard/:stepName"
                        component={() => (
                            <CheckAuthorization>
                                <UserWizard />
                            </CheckAuthorization>
                        )}
                    />
                    <Route component={NotFound} />
                </Switch>
            </TemplateContext>
        </LookUpContext>
    );
};

export default Routes;
